import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Phone, Globe, LogIn, User as UserIcon, LogOut, LayoutDashboard, Shield, Car, Bell } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { API } from "@/lib/api";


const LOGO = "/brand/vd-avatar-256.png";
const PHONE = "+33783977370";
const PHONE_DISPLAY = "+33 7 83 97 73 70";

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setLangOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  // Poll for pending reservations (admin/driver) OR pending signatures (client) - every 20s
  const [pendingCount, setPendingCount] = useState(0);
  const [clientSignCount, setClientSignCount] = useState(0);
  useEffect(() => {
    if (!user) return;
    const isStaff = user.role === "admin" || user.role === "driver";
    let prevCount = 0;
    let prevSign = 0;
    const fetchNotif = async () => {
      try {
        if (isStaff) {
          const res = await axios.get(`${API}/admin/notifications`, { withCredentials: true });
          const c = res.data.pending_count || 0;
          if (c > prevCount && prevCount > 0) {
            try {
              if ("Notification" in window && Notification.permission === "granted") {
                new Notification("Nouvelle réservation VlaDriver", { body: `${c - prevCount} nouvelle(s) réservation(s) en attente`, icon: LOGO });
              }
            } catch (_e1) { /* Notification API not available */ }
          }
          prevCount = c;
          setPendingCount(c);
        } else {
          // Client — pending signatures
          const res = await axios.get(`${API}/client/notifications`, { withCredentials: true });
          const c = res.data.pending_signature_count || 0;
          if (c > prevSign && prevSign > 0) {
            try {
              if ("Notification" in window && Notification.permission === "granted") {
                new Notification("Votre devis est prêt · VlaDriver", { body: "Signez votre devis pour confirmer votre réservation.", icon: LOGO });
              }
            } catch (_e2) { /* Notification API not available */ }
          }
          prevSign = c;
          setClientSignCount(c);
        }
      } catch (_e3) { /* silent — retry next tick */ }
    };
    fetchNotif();
    const id = setInterval(fetchNotif, 20000);
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    return () => clearInterval(id);
  }, [user]);

  const langs = [
    { code: "fr", label: "FR", name: "Français" },
    { code: "en", label: "EN", name: "English" },
    { code: "ro", label: "RO", name: "Română" },
  ];
  const current = langs.find(l => l.code === i18n.language) || langs[0];

  const navLinks = [
    { to: "/vtc-nice", label: "VTC Nice" },
    { to: "/vtc-aeroport-nice", label: t("nav.services") },
    { to: "/tarifs", label: t("nav.pricing") },
    { to: "/galerie", label: "Galerie" },
    { to: "/mon-parcours", label: "Fidélité" },
    { to: "/blog", label: t("nav.blog") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-glass shadow-2xl" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo — self-contained circular badge (VD + VLADRIVER + VTC RIVIERA baked in) */}
          <Link to="/" data-testid="nav-home-logo" className="flex items-center group">
            <img src={LOGO} alt="VlaDriver VTC Riviera" width="56" height="56" fetchPriority="high" decoding="async" className="h-12 w-12 md:h-14 md:w-14 rounded-full ring-1 ring-[#D4AF37]/30 group-hover:ring-[#D4AF37] transition-all" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-wider transition-colors ${
                    isActive ? "text-[#D4AF37]" : "text-white/80 hover:text-[#D4AF37]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <a
              href={`tel:${PHONE}`}
              data-testid="header-phone-btn"
              className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
            >
              <Phone size={16} />
              <span className="text-sm font-medium">{PHONE_DISPLAY}</span>
            </a>

            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                data-testid="language-switcher-btn"
                className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors text-white"
              >
                <Globe size={14} />
                <span className="text-xs font-semibold">{current.label}</span>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-[#0F1115] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onClick={() => { i18n.changeLanguage(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors ${
                        i18n.language === l.code ? "text-[#D4AF37] bg-white/5" : "text-white/80"
                      }`}
                    >
                      {l.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/#booking"
              data-testid="header-book-btn"
              className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full btn-gold text-sm"
            >
              {t("nav.book")}
            </Link>

            {/* Client — "Mes réservations" button with badge for pending signatures */}
            {user && user.role === "client" && (
              <Link
                to="/mon-compte"
                data-testid="header-my-reservations-btn"
                className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all relative"
              >
                <Bell size={14} />
                <span className="text-xs font-semibold uppercase tracking-wider">Mes réservations</span>
                {clientSignCount > 0 && (
                  <span data-testid="client-sign-badge" className="ml-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 px-1 flex items-center justify-center">{clientSignCount}</span>
                )}
              </Link>
            )}

            {/* Admin/Driver — separate bell with notification badge */}
            {user && (user.role === "admin" || user.role === "driver") && (
              <Link
                to={user.role === "admin" ? "/admin" : "/chauffeur"}
                data-testid="header-notif-bell"
                className={`hidden md:inline-flex items-center justify-center relative w-10 h-10 rounded-full border transition-all ${
                  pendingCount > 0
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "border-white/15 text-white/70 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
                }`}
              >
                <Bell size={16} className={pendingCount > 0 ? "animate-pulse" : ""} />
                {pendingCount > 0 && (
                  <span data-testid="admin-notif-badge" className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 px-1 flex items-center justify-center">{pendingCount}</span>
                )}
              </Link>
            )}

            {/* User menu / Login */}
            {user ? (
              <div className="relative hidden md:block">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  data-testid="user-menu-btn"
                  className="flex items-center gap-2 rounded-full bg-white/5 hover:bg-white/10 p-1.5 pr-3"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#D4AF37] flex items-center justify-center text-black text-xs font-bold">{user.name?.charAt(0)}</div>
                  )}
                  <span className="text-xs text-white/80 max-w-[80px] truncate">{user.name?.split(' ')[0]}</span>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-[#0F1115] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/5">
                      <div className="text-xs text-white">{user.name}</div>
                      <div className="text-[11px] text-white/40 truncate">{user.email}</div>
                      <div className="mt-1 text-[10px] text-[#D4AF37] uppercase tracking-wider">{user.role}</div>
                    </div>
                    <Link to="/mon-compte" data-testid="menu-mon-compte" className="block px-4 py-2.5 text-sm text-white/80 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] flex items-center gap-2">
                      <UserIcon size={14} /> Mon compte
                    </Link>
                    {(user.role === "driver" || user.role === "admin") && (
                      <Link to="/chauffeur" data-testid="menu-chauffeur" className="block px-4 py-2.5 text-sm text-white/80 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] flex items-center gap-2">
                        <Car size={14} /> Espace Chauffeur
                      </Link>
                    )}
                    {user.role === "admin" && (
                      <Link to="/admin" data-testid="menu-admin" className="block px-4 py-2.5 text-sm text-white/80 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] flex items-center gap-2">
                        <Shield size={14} /> Administration
                      </Link>
                    )}
                    <button onClick={logout} data-testid="menu-logout" className="w-full text-left px-4 py-2.5 text-sm text-white/60 hover:bg-red-500/10 hover:text-red-400 flex items-center gap-2 border-t border-white/5">
                      <LogOut size={14} /> Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={login}
                data-testid="header-login-btn"
                className="inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 md:py-2.5 rounded-full bg-[#D4AF37]/15 hover:bg-[#D4AF37]/25 border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[#D4AF37] text-xs md:text-sm font-semibold uppercase tracking-wider transition-all"
              >
                <LogIn size={14} strokeWidth={2} />
                <span>Connexion</span>
              </button>
            )}

            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white p-2"
              aria-label="Menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden bg-[#0F1115] border-t border-white/10 -mx-6 px-6 py-6 space-y-3">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${link.to.slice(1)}`}
                className={({ isActive }) =>
                  `block py-2 text-base uppercase tracking-wide ${isActive ? "text-[#D4AF37]" : "text-white"}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <a href={`tel:${PHONE}`} className="flex items-center gap-2 py-2 text-[#D4AF37]">
              <Phone size={16} /> {PHONE_DISPLAY}
            </a>

            {/* Mobile — User account section */}
            {user ? (
              <div className="pt-3 mt-3 border-t border-white/10 space-y-2">
                <div className="flex items-center gap-3 py-1">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-black text-xs font-bold">{user.name?.charAt(0)}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{user.name}</div>
                    <div className="text-[10px] text-[#D4AF37] uppercase tracking-wider">{user.role}</div>
                  </div>
                </div>
                <Link to="/mon-compte" data-testid="mobile-menu-mon-compte" className="flex items-center gap-2 py-2 text-white/80 hover:text-[#D4AF37]">
                  <UserIcon size={14} /> Mes réservations
                  {user.role === "client" && clientSignCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 px-1 flex items-center justify-center">{clientSignCount}</span>
                  )}
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="mobile-menu-admin" className="flex items-center gap-2 py-2 text-white/80 hover:text-[#D4AF37]">
                    <Shield size={14} /> Administration
                    {pendingCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 px-1 flex items-center justify-center">{pendingCount}</span>
                    )}
                  </Link>
                )}
                {(user.role === "driver" || user.role === "admin") && (
                  <Link to="/chauffeur" data-testid="mobile-menu-chauffeur" className="flex items-center gap-2 py-2 text-white/80 hover:text-[#D4AF37]">
                    <Car size={14} /> Espace Chauffeur
                  </Link>
                )}
                <button onClick={logout} data-testid="mobile-menu-logout" className="flex items-center gap-2 py-2 text-red-400 hover:text-red-300">
                  <LogOut size={14} /> Déconnexion
                </button>
              </div>
            ) : (
              <button
                onClick={login}
                data-testid="mobile-menu-login-btn"
                className="w-full flex items-center justify-center gap-2 py-3 mt-3 rounded-full border-2 border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 text-[#D4AF37] font-semibold uppercase tracking-wider text-sm"
              >
                <LogIn size={16} strokeWidth={2} /> Se connecter
              </button>
            )}

            <Link to="/#booking" className="block w-full text-center btn-gold px-5 py-3 rounded-full mt-4">
              {t("nav.book")}
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
